import {
  FormControl,
  FormHelperText,
  InputAdornment,
  type InputBaseProps,
  OutlinedInput,
  Stack,
} from '@mui/material';
import type { UseFormRegister } from 'react-hook-form';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';

type IProps = Omit<
  InputBaseProps,
  'name' | 'autoComplete' | 'size' | 'fullWidth' | 'sx' | 'value' | 'onChange'
> & {
  register: UseFormRegister<any>;
  name: string;
  errors: any;
  label?: string;
  suffix?: string;
  prefix?: string;
  helpText?: string;
  actions?: React.ReactNode;
};

export const RaTextField: React.FC<IProps> = ({
  name,
  errors,
  label,
  register,
  required = false,
  multiline = false,
  minRows,
  suffix,
  prefix,
  helpText,
  actions,
  ...inputProps
}) => {
  const { t } = useLocale();
  return (
    <FormControl fullWidth error={!!errors[name]}>
      {actions ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          {label && (
            <RaLabel label={label} required={required} sx={{ mb: 0 }} />
          )}
          {actions}
        </Stack>
      ) : (
        label && <RaLabel label={label} required={required} />
      )}
      <OutlinedInput
        autoComplete="off"
        size="small"
        fullWidth
        sx={{
          background: 'var(--mui-palette-background-paper)',
          '& .MuiInputAdornment-positionStart': {
            marginTop: '0px !important',
          },
        }}
        required={required}
        multiline={multiline}
        minRows={multiline ? minRows ?? 3 : undefined}
        endAdornment={
          suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : null
        }
        startAdornment={
          prefix ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : null
        }
        {...inputProps}
        {...register(name, {
          required: required ? t('This field is required') : required,
          // Prevent only white spaces
          pattern: {
            value: /\S+/,
            message: t('This field contains only white spaces'),
          },
        })}
      />
      {(errors[name]?.message || helpText) && (
        <FormHelperText>{errors[name]?.message ?? helpText}</FormHelperText>
      )}
    </FormControl>
  );
};
